import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux'
import { Controller, FieldValues, useForm } from 'react-hook-form'
import moment from 'moment'
import { useDebouncedCallback } from 'use-debounce'
import 'react-rater/lib/react-rater.css'
import { formatDate } from 'Constants/formatDate'
import { respondToReview } from 'Redux/actions/reviewsActions'
import { getCampaignDetails } from 'Redux/actions/campaignActions'
import { getAmountOfCampaigns } from 'Redux/selectors/campaigns'
import { getUserSelector } from 'Redux/selectors/auth'
import { GreenBtn } from 'Stories/Button/Button.stories'

import { StarYellowIcon, StarGreyIcon } from 'Stories/Icon/Icon.stories'
import { Mobile, Desktop } from 'Shared/components/Responsive'
import unknownAvatar from 'Shared/img/unknownAvatar.png'
import ava from 'Shared/img/ava.png'
import { capitalizeFirstLetter } from 'Helpers/layoutHelpers'
import { artistReviewSentEvent } from 'Helpers/tagManager'
import { offensiveValue, poorValue, thankValue } from 'Constants/constants'
import { numberFormatCommas, numberFormatSpaces } from 'Constants/functions'
import { ProperGenreOrLanguage, CampaignPlatformTypeEnum } from 'Types/index'
import SelectResponseRating from '../SelectResponseRating'
import SelectResponseText from '../SelectResponseText'
import { ReviewStatus } from '../../props'
import { DataNotReceivedContainer, DataNotReceivedLabel } from '../../styles'

import {
  AuthorContainer,
  AuthorInfoContainer,
  AuthorName,
  AuthorStatsContainer,
  Avatar,
  AvatarContainer,
  HiddenPlaylistText,
  MatchesAndResultCol,
  MatchesAndResultContainer,
  MatchesAndResultRow,
  MissedReviewContainer,
  MissedReviewDescription,
  MissedReviewRefundAmount,
  MissedReviewRefundContainer,
  MissedReviewRefundLabel,
  OpenOnSpotify,
  OpenOnSpotifyLink,
  RatingContainer,
  RespondedContainer,
  RespondedFeedback,
  RespondedRating,
  RespondedTitle,
  ResponseContainer,
  ResponseForm,
  ReviewContainer,
  ReviewContentContainer,
  ReviewMainContainer,
  ReviewMainSectionLabel,
  ReviewMainSectionText,
  ReviewSubSectionLabel,
  ReviewSubSectionText,
  ToggleMatches,
} from './styles'
import { CampaignRatingProps, RateReviewValues } from './props'

const getStreamsListenersStats = (
  streams: number | undefined,
  listeners: number | undefined,
  t: TFunction<'translation'>
) => {
  if (!streams && !listeners) {
    return (
      <DataNotReceivedContainer>
        <DataNotReceivedLabel>
          {t('campaignResultsPage.collectingData')}
        </DataNotReceivedLabel>
      </DataNotReceivedContainer>
    )
  }

  return (
    <ReviewSubSectionText>
      {`${numberFormatCommas(streams)} / 
      ${numberFormatCommas(listeners)}`}
    </ReviewSubSectionText>
  )
}

const CampaignRating: FunctionComponent<CampaignRatingProps> = ({
  review,
  campaignID,
  isSFAConnected,
}) => {
  const {
    Curator,
    review_date,
    id: reviewId,
    Playlist,
    matched,
    curator_rating,
    review_feedback,
    review_refund,
    comment,
    review_status,
  } = review

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const campaignDetails = useSelector(
    (state: RootStateOrAny) => state.campaignDetails.details
  )

  const user = useSelector(getUserSelector)
  const amountOfCampaigns = useSelector(getAmountOfCampaigns)

  const isMissed = useMemo(
    () =>
      // @ts-ignore
      review_status === ReviewStatus.missed ||
      // @ts-ignore
      review_status === ReviewStatus.canceled ||
      // @ts-ignore
      review_status === ReviewStatus.skipped,
    [review_status]
  )

  const { handleSubmit, control, watch } = useForm<FieldValues>({
    mode: 'onChange',
  })

  const watchRatingSelect = watch('responseReviewRatingSelect', '')
  const watchReviewSelect = watch('responseReviewSelect', '')

  const [isDefaultImage, setIsDefaultImage] = useState<boolean>(false)
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false)
  const [isMatchedOpened, setIsMatchedOpened] = useState<boolean>(false)

  const onToggleMatches = () => {
    setIsMatchedOpened(!isMatchedOpened)
  }

  const enabledSubmit: boolean = useMemo(
    () => !!watchRatingSelect && !!watchReviewSelect && !submitDisabled,
    [watchRatingSelect, watchReviewSelect, submitDisabled]
  )

  const onImageLoadError = () => {
    setIsDefaultImage(true)
  }

  const onSubmit = (values: RateReviewValues) => {
    dispatch(
      respondToReview(
        values.responseReviewSelect,
        reviewId,
        values.responseReviewRatingSelect,
        (error: string) => {
          if (!error) {
            dispatch(
              getCampaignDetails(campaignID, (error, res) => {
                artistReviewSentEvent(
                  campaignDetails.type,
                  CampaignPlatformTypeEnum.SPOTIFY,
                  campaignDetails.type === 'first' ? 1 : amountOfCampaigns,
                  campaignDetails.id,
                  user.id,
                  values.responseReviewRatingSelect,
                  values.responseReviewSelect,
                  reviewId,
                  res.is_feed
                )
              })
            )
          }
        }
      )
    )
  }

  const [debouncedCallback] = useDebouncedCallback(
    (values: RateReviewValues) => {
      onSubmit(values)
      setSubmitDisabled(true)
    },
    1000
  )

  const renderRespond = () => {
    switch (review_feedback) {
      case thankValue:
      default:
        return t('campaignResultsPage.thankYou')
      case offensiveValue:
        return t('campaignResultsPage.soundsOffensive')
      case poorValue:
        return t('campaignResultsPage.notUsefullFeedback')
    }
  }

  const renderRating = (rating: number) => {
    const ratingArray = Array.from({ length: 5 })
    for (let i = 0; i !== +rating; i += 1) {
      ratingArray[i] = true
    }
    return ratingArray.map(el => (el ? <StarYellowIcon /> : <StarGreyIcon />))
  }

  const renderGenresLanguages = (items: Array<ProperGenreOrLanguage>) =>
    items.map(
      (el, id) =>
        `${capitalizeFirstLetter(el.name)}${
          id === items.length - 1 ? '' : ', '
        }`
    )

  const avatar = useMemo(() => {
    if (isMissed || !Curator) {
      return unknownAvatar
    }
    if (isDefaultImage) {
      return ava
    }
    if (Curator?.User?.user_photo_url) {
      return Curator?.User?.user_photo_url
    }
    return ''
  }, [isMissed, Curator, isDefaultImage])

  const curatorName = useMemo(() => {
    if (isMissed || !Curator) {
      return t('campaignResultsPage.unknownCurator')
    }
    return `${capitalizeFirstLetter(Curator?.User?.first_name)} ${
      Curator?.User?.last_name
        ? capitalizeFirstLetter(Curator.User.last_name)
        : ''
    }`
  }, [isMissed, Curator])

  const renderMatchedPlaylists = useCallback(
    () =>
      matched &&
      matched.map(el => (
        <MatchesAndResultCol key={el.id}>
          <ReviewMainSectionLabel>
            {t('campaignResultsPage.playlistMatches')}
          </ReviewMainSectionLabel>
          <MatchesAndResultRow>
            <ReviewSubSectionLabel short>
              {`${t('campaignResultsPage.playlist')}:`}
            </ReviewSubSectionLabel>
            <ReviewSubSectionText>{el.playlist_name}</ReviewSubSectionText>
          </MatchesAndResultRow>
          <MatchesAndResultRow>
            <ReviewSubSectionLabel short>
              {`${t('campaignResultsPage.genres')}:`}
            </ReviewSubSectionLabel>
            <ReviewSubSectionText>
              {renderGenresLanguages(el.genres)}
            </ReviewSubSectionText>
          </MatchesAndResultRow>
          <MatchesAndResultRow>
            <ReviewSubSectionLabel short>
              {`${t('campaignResultsPage.vocal')}:`}
            </ReviewSubSectionLabel>
            <ReviewSubSectionText>
              {renderGenresLanguages(el.languages)}
            </ReviewSubSectionText>
          </MatchesAndResultRow>
        </MatchesAndResultCol>
      )),
    [matched]
  )

  const playlistsLabel =
    +Curator?.playlists_count !== 1
      ? t('campaignResultsPage.playlistsLabel').toLowerCase()
      : t('campaignResultsPage.playlistLabel').toLowerCase()
  const followersLabel =
    +Curator?.followers_count !== 1
      ? t('campaignResultsPage.followers').toLowerCase()
      : t('campaignResultsPage.follower').toLowerCase()

  const daysInPlaylist = moment().diff(moment(review_date), 'days')
  const reviewDate = moment(review_date).format(formatDate.formatDateMonthFull)

  // @ts-ignore
  return (
    <ReviewContainer>
      <ReviewContentContainer>
        <AuthorContainer>
          <AvatarContainer>
            <Avatar alt="avatar" src={avatar} onError={onImageLoadError} />
          </AvatarContainer>
          <AuthorInfoContainer>
            <AuthorName>{curatorName}</AuthorName>
            <RatingContainer>
              {renderRating(Curator ? Curator?.rating : 0)}
            </RatingContainer>
            {!isMissed && (
              <AuthorStatsContainer>
                {`${
                  Curator?.playlists_count
                } ${playlistsLabel}, ${numberFormatSpaces(
                  +Curator?.followers_count
                )} ${followersLabel}`}
              </AuthorStatsContainer>
            )}
          </AuthorInfoContainer>
        </AuthorContainer>
        {!isMissed && (
          <ReviewMainContainer>
            <ReviewMainSectionLabel>
              {t('campaignResultsPage.review')}
            </ReviewMainSectionLabel>
            <ReviewMainSectionText>{comment}</ReviewMainSectionText>
            {Playlist?.playlist_url && (
              <MatchesAndResultContainer>
                <MatchesAndResultCol>
                  <ReviewMainSectionLabel>
                    {t('campaignResultsPage.playlistMatches')}
                  </ReviewMainSectionLabel>
                  <MatchesAndResultRow>
                    <ReviewSubSectionLabel short>
                      {`${t('campaignResultsPage.playlist')}:`}
                    </ReviewSubSectionLabel>
                    <ReviewSubSectionText>
                      {Playlist?.is_hidden ? (
                        <HiddenPlaylistText>
                          {`${t('campaignResultsPage.hiddenByCurator')}`}
                        </HiddenPlaylistText>
                      ) : (
                        Playlist?.playlist_name
                      )}
                    </ReviewSubSectionText>
                  </MatchesAndResultRow>
                  <MatchesAndResultRow>
                    <ReviewSubSectionLabel short>
                      {`${t('campaignResultsPage.genres')}:`}
                    </ReviewSubSectionLabel>
                    <ReviewSubSectionText>
                      {renderGenresLanguages(Playlist.genres)}
                    </ReviewSubSectionText>
                  </MatchesAndResultRow>
                  <MatchesAndResultRow>
                    <ReviewSubSectionLabel short>
                      {`${t('campaignResultsPage.vocal')}:`}
                    </ReviewSubSectionLabel>
                    <ReviewSubSectionText>
                      {renderGenresLanguages(Playlist.languages)}
                    </ReviewSubSectionText>
                  </MatchesAndResultRow>
                  <Desktop>
                    <OpenOnSpotify>
                      {t('campaignResultsPage.openPlaylistOn')}
                      <OpenOnSpotifyLink
                        target="_blank"
                        href={Playlist.playlist_url}
                      >
                        {t('campaignResultsPage.spotify')}
                      </OpenOnSpotifyLink>
                    </OpenOnSpotify>
                  </Desktop>
                </MatchesAndResultCol>
                <MatchesAndResultCol>
                  <ReviewMainSectionLabel>
                    {t('campaignResultsPage.result')}
                  </ReviewMainSectionLabel>
                  <MatchesAndResultRow>
                    <ReviewSubSectionLabel>
                      {`${t('campaignResultsPage.streamsListeners')}:`}
                    </ReviewSubSectionLabel>

                    {!isSFAConnected && (
                      <DataNotReceivedContainer>
                        <DataNotReceivedLabel>
                          {t('campaignResultsPage.dataNotReceived')}
                        </DataNotReceivedLabel>
                      </DataNotReceivedContainer>
                    )}

                    {isSFAConnected &&
                      getStreamsListenersStats(
                        Playlist.streams_count,
                        Playlist.listeners_count,

                        t
                      )}
                  </MatchesAndResultRow>

                  <MatchesAndResultRow>
                    <ReviewSubSectionLabel>
                      {`${t('campaignResultsPage.daysInPlaylist')}:`}
                    </ReviewSubSectionLabel>
                    <ReviewSubSectionText>
                      {`${daysInPlaylist !== 0 ? daysInPlaylist : ''} ${t(
                        'campaignResultsPage.from'
                      )} ${reviewDate}`}
                    </ReviewSubSectionText>
                  </MatchesAndResultRow>
                  <Mobile>
                    <OpenOnSpotify>
                      {t('campaignResultsPage.openPlaylistOn')}
                      <OpenOnSpotifyLink href={Playlist.playlist_url}>
                        {t('campaignResultsPage.spotify')}
                      </OpenOnSpotifyLink>
                    </OpenOnSpotify>
                  </Mobile>
                </MatchesAndResultCol>
              </MatchesAndResultContainer>
            )}
            {matched && isMatchedOpened && renderMatchedPlaylists()}
            {matched && (
              <ToggleMatches onClick={onToggleMatches}>
                {isMatchedOpened
                  ? t('campaignResultsPage.hideMatches')
                  : t('campaignResultsPage.showMatches')}
              </ToggleMatches>
            )}
            <ResponseContainer>
              {review_feedback ? (
                <RespondedContainer>
                  <RespondedTitle>
                    {t('campaignResultsPage.yourResponse')}
                  </RespondedTitle>
                  <RespondedRating>
                    {renderRating(curator_rating)}
                  </RespondedRating>
                  <RespondedFeedback>{`${t(
                    'campaignResultsPage.youSaid'
                  )} "${renderRespond()}"`}</RespondedFeedback>
                </RespondedContainer>
              ) : (
                <ResponseForm onSubmit={handleSubmit(debouncedCallback)}>
                  <Controller
                    control={control}
                    name="responseReviewRatingSelect"
                    render={({ field: { onChange, value, ref } }) => (
                      <SelectResponseRating
                        ref={ref}
                        value={value}
                        onChange={onChange}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="responseReviewSelect"
                    render={({ field: { onChange, value, ref } }) => (
                      <SelectResponseText
                        ref={ref}
                        value={value}
                        onChange={onChange}
                      />
                    )}
                  />
                  <Desktop>
                    {enabledSubmit ? (
                      <GreenBtn
                        color="white"
                        type="submit"
                        // @ts-ignore
                        onClick={handleSubmit}
                      >
                        {t('campaignResultsPage.send')}
                      </GreenBtn>
                    ) : (
                      // @ts-ignore
                      <GreenBtn color="greyBorderDisabled" disabled>
                        {t('campaignResultsPage.send')}
                      </GreenBtn>
                    )}
                  </Desktop>
                  <Mobile>
                    {enabledSubmit ? (
                      <GreenBtn
                        color="whiteFull"
                        type="submit"
                        // @ts-ignore
                        onClick={handleSubmit}
                      >
                        {t('campaignResultsPage.send')}
                      </GreenBtn>
                    ) : (
                      // @ts-ignore
                      <GreenBtn color="greyBorderDisabledFull" disabled>
                        {t('campaignResultsPage.send')}
                      </GreenBtn>
                    )}
                  </Mobile>
                </ResponseForm>
              )}
            </ResponseContainer>
          </ReviewMainContainer>
        )}
        {isMissed && (
          <MissedReviewContainer>
            <MissedReviewDescription>
              {t('campaignResultsPage.missedReviewDescription')}
            </MissedReviewDescription>
            <MissedReviewRefundContainer>
              <MissedReviewRefundLabel>
                {`${t('campaignResultsPage.refundLabel')}: `}
              </MissedReviewRefundLabel>
              <MissedReviewRefundAmount>{`$${review_refund}`}</MissedReviewRefundAmount>
            </MissedReviewRefundContainer>
          </MissedReviewContainer>
        )}
      </ReviewContentContainer>
    </ReviewContainer>
  )
}

export default CampaignRating
